import { useMemo } from 'react';
import { Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EllipsisOutlined } from '@ant-design/icons';
import { useCustomerConfig, useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { PromoCell, renderNumberCell } from '../../cellRenderers';
import type {
    CellValue,
    Granularity,
    KpiViewType,
    MetricType,
    PeriodColumn,
    PlanMetricRow,
    PromoMetricRow,
    SubMode,
} from '../../types';

const PERIOD_COL_WIDTH: Record<Granularity, number> = {
    W: 160,
    M: 130,
    Q: 180,
};

const NUMERIC_SORTER = (a: any, b: any, key: string): number => {
    const av = typeof a?.[key] === 'number' ? a[key] : 0;
    const bv = typeof b?.[key] === 'number' ? b[key] : 0;
    return av - bv;
};

interface PlanMetricColumnsArgs {
    subMode: SubMode;
    dimension: string;
    periods: PeriodColumn[];
    granularity: Granularity;
    getColumnSortOrder: (key: string) => 'ascend' | 'descend' | null;
    onClickGranularity: () => void;
}

export function usePlanMetricColumns(args: PlanMetricColumnsArgs): ColumnsType<PlanMetricRow> {
    const { subMode, dimension, periods, granularity, getColumnSortOrder, onClickGranularity } = args;
    const { metricPageConfig } = useTpoGenConfig();
    const planDimLabels = useMemo(
        () => new Map(metricPageConfig.planMetricDimensions.map((d) => [d.value, d.label])),
        [metricPageConfig],
    );

    return useMemo<ColumnsType<PlanMetricRow>>(() => {
        const columns: ColumnsType<PlanMetricRow> = [];
        const periodWidth = PERIOD_COL_WIDTH[granularity];

        if (subMode === 'dimension') {
            columns.push({
                title: planDimLabels.get(dimension) ?? 'Dimension',
                dataIndex: 'attributeDesc',
                key: 'label',
                fixed: 'left',
                width: 200,
                sorter: true,
                sortOrder: getColumnSortOrder('label'),
                render: (value: string) => <span className="font-medium">{value}</span>,
            });
        } else {
            columns.push({
                title: 'Metric',
                dataIndex: 'kpi_name',
                key: 'label',
                fixed: 'left',
                width: 220,
                sorter: true,
                sortOrder: getColumnSortOrder('label'),
                render: (value: string) => <span className="font-medium">{value}</span>,
            });
        }

        columns.push({
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 140,
            sorter: true,
            sortOrder: getColumnSortOrder('total'),
            align: 'right',
            fixed: 'left',
            render: (value: number) => renderNumberCell(value),
        });

        for (const period of periods) {
            columns.push({
                title: period.label,
                dataIndex: period.key,
                key: period.key,
                width: periodWidth,
                sorter: true,
                sortOrder: getColumnSortOrder(period.key),
                align: 'right',
                ellipsis: true,
                render: (value: number) => renderNumberCell(value),
            });
        }

        columns.push({
            title: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            { key: 'change_calendar_view', label: 'Change Calendar View', onClick: onClickGranularity },
                        ],
                    }}
                >
                    <button
                        type="button"
                        aria-label="Change Calendar view"
                        className="flex items-center justify-center w-full h-full p-0 m-0 bg-transparent border-0 cursor-pointer"
                    >
                        <EllipsisOutlined />
                    </button>
                </Dropdown>
            ),
            dataIndex: '__more',
            key: '__more',
            width: 40,
            fixed: 'right',
            align: 'center',
            render: () => null,
        });

        return columns;
    }, [subMode, dimension, periods, granularity, getColumnSortOrder, onClickGranularity, planDimLabels]);
}

interface PromoMetricColumnsArgs {
    dimensions: string[];
    kpis: string[];
    kpiViewType: KpiViewType;
    metricType: MetricType;
    getColumnSortOrder: (key: string) => 'ascend' | 'descend' | null;
    onClickKpiView: () => void;
    onClickMetric: () => void;
}

export function usePromoMetricColumns(args: PromoMetricColumnsArgs): ColumnsType<PromoMetricRow> {
    const { dimensions, kpis, kpiViewType, metricType, getColumnSortOrder, onClickKpiView, onClickMetric } = args;
    const { getKpiByKey, getKpiName } = useCustomerConfig();
    const { metricPageConfig } = useTpoGenConfig();
    const promoDimLabels = useMemo(
        () => new Map(metricPageConfig.promoMetricDimensions.map((d) => [d.value, d.label])),
        [metricPageConfig],
    );

    return useMemo<ColumnsType<PromoMetricRow>>(() => {
        const columns: ColumnsType<PromoMetricRow> = [];
        // Metric type (abs/percent) only applies to the actuals vs pre-snapshot comparison view.
        const isComparisonView = kpiViewType === 'actuals_vs_pre_snapshot';

        dimensions.forEach((dim, idx) => {
            columns.push({
                title: promoDimLabels.get(dim) ?? dim,
                dataIndex: dim,
                key: dim,
                fixed: idx === 0 ? 'left' : undefined,
                width: 180,
                sorter: true,
                sortOrder: getColumnSortOrder(dim),
                render: (value: string) => <span className="font-medium">{value}</span>,
            });
        });

        for (const kpiKey of kpis) {
            const kpi = getKpiByKey(kpiKey);
            columns.push({
                title: kpi?.name ?? getKpiName(kpiKey),
                dataIndex: kpiKey,
                key: kpiKey,
                width: isComparisonView ? 280 : 160,
                sorter:
                    kpi?.sortable ?? true
                        ? (a, b) =>
                              NUMERIC_SORTER(
                                  {
                                      [kpiKey]:
                                          (a as PromoMetricRow)[kpiKey] &&
                                          typeof (a as PromoMetricRow)[kpiKey] === 'object'
                                              ? ((a as PromoMetricRow)[kpiKey] as CellValue).current
                                              : 0,
                                  },
                                  {
                                      [kpiKey]:
                                          (b as PromoMetricRow)[kpiKey] &&
                                          typeof (b as PromoMetricRow)[kpiKey] === 'object'
                                              ? ((b as PromoMetricRow)[kpiKey] as CellValue).current
                                              : 0,
                                  },
                                  kpiKey,
                              )
                        : false,
                sortOrder: getColumnSortOrder(kpiKey),
                align: 'right',
                render: (value: CellValue) => (
                    <PromoCell value={value} kpiViewType={kpiViewType} metricType={metricType} />
                ),
            });
        }

        columns.push({
            title: (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            { key: 'change_kpi_view', label: 'Change KPI view', onClick: onClickKpiView },
                            {
                                key: 'change_metric',
                                label: 'Change Metric',
                                onClick: onClickMetric,
                                disabled: !isComparisonView,
                            },
                        ],
                    }}
                >
                    <button
                        type="button"
                        aria-label="Change KPI view or metric"
                        className="flex items-center justify-center w-full h-full p-0 m-0 bg-transparent border-0 cursor-pointer"
                    >
                        <EllipsisOutlined />
                    </button>
                </Dropdown>
            ),
            dataIndex: '__more',
            key: '__more',
            width: 40,
            fixed: 'right',
            align: 'center',
            render: () => null,
        });

        return columns;
    }, [dimensions, kpis, kpiViewType, metricType, getColumnSortOrder, onClickKpiView, onClickMetric, getKpiByKey, getKpiName, promoDimLabels]);
}
